/**
 * Small shared pieces for the V1 read views: async state boxes, table
 * styles, badges, and formatters. Views stay thin; anything richer waits
 * for real usage feedback.
 *
 * THE LEADLOOP VOCABULARY NO LONGER LIVES HERE. The lifecycle badge, the
 * Verified tag, the intake state badge, the loop-reason badge, the action
 * label, and the timestamp formatters moved to
 * `@command-center/oneup-portal-ui` so One Up's LeadLoop renders from the same
 * code rather than from a near-copy that had already started to drift. They are
 * re-exported below, unchanged, so every import in this app keeps working and
 * there is still exactly one implementation.
 */

import type { ReactNode } from 'react';
import { Badge, cn } from '@command-center/ui-system';
import { ApiClientError } from '../lib/api';

export {
  IntakeStateBadge,
  LeadStatusBadge,
  LoopReasonBadge,
  ReasonBadge,
  TimeStamp,
  VerifiedTag,
  formatDateTime,
  formatExactDateTime,
  formatRelativeTime,
  formatSourceLabel,
  isInformativeReason,
  loopActionLabel,
} from '@command-center/oneup-portal-ui';

import { formatDateTime } from '@command-center/oneup-portal-ui';

export function StateBox({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="max-w-md rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
        {children}
      </div>
    </div>
  );
}

export function LoadingState() {
  return <StateBox>Loading…</StateBox>;
}

/**
 * The API answers 503 airtable_not_configured / supabase_not_configured
 * until the install is wired up — surface that plainly instead of a
 * generic failure.
 */
export function ErrorState({ error }: { error: unknown }) {
  if (error instanceof ApiClientError) {
    const notWired = error.status === 503 || error.code === 'not_implemented';
    return (
      <StateBox>
        <p className={cn('font-medium', notWired ? 'text-foreground' : 'text-destructive')}>
          {notWired ? 'Not connected yet' : 'Something went wrong'}
        </p>
        <p className="mt-2">{error.message}</p>
      </StateBox>
    );
  }
  return (
    <StateBox>
      <p className="font-medium text-destructive">Something went wrong</p>
      <p className="mt-2">{error instanceof Error ? error.message : 'Unknown error'}</p>
    </StateBox>
  );
}

export function EmptyState({ label }: { label: string }) {
  return <StateBox>{label}</StateBox>;
}

export const th = 'whitespace-nowrap px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground';
export const td = 'px-3 py-2.5 align-top text-[13px] text-foreground';

export function TableShell({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full min-w-[720px] border-collapse">{children}</table>
    </div>
  );
}

/* ------------------------------------------------------------ table cells */

/** The one em-dash. Every table renders a missing value the same way. */
export function Empty() {
  return <span className="text-muted-foreground">—</span>;
}

/** Value or em-dash, truncated to a sane column width. */
export function Cell({ value, width = '22ch' }: { value?: ReactNode; width?: string }) {
  if (value === undefined || value === null || value === '') return <Empty />;
  return (
    <span className="block truncate" style={{ maxWidth: width }} title={typeof value === 'string' ? value : undefined}>
      {value}
    </span>
  );
}

export function DateCell({ value }: { value?: string | null }) {
  if (!value) return <Empty />;
  return <span className="whitespace-nowrap tabular-nums">{formatDateTime(value)}</span>;
}

export function CountCell({ value, suffix }: { value: number; suffix?: string }) {
  if (!value) return <Empty />;
  return (
    <span className="whitespace-nowrap tabular-nums">
      {value}
      {suffix ? ` ${suffix}` : ''}
    </span>
  );
}

/** Consistent row-opening affordance: the record's name is the link. */
export function OpenCell({
  title,
  subtitle,
  onOpen,
  width = '260px',
}: {
  title: string;
  subtitle?: ReactNode;
  onOpen: () => void;
  width?: string;
}) {
  return (
    <button type="button" onClick={onOpen} className="max-w-full text-left" style={{ maxWidth: width }}>
      <span className="block truncate font-medium text-primary hover:underline">{title}</span>
      {subtitle && <span className="block truncate text-[12px] text-muted-foreground">{subtitle}</span>}
    </button>
  );
}

/* ---------------------------------------------------------------- badges */

/**
 * Generic status badge for records with an open-ended status vocabulary
 * (Organizations, whose choices are set per-install). Leads must
 * use LeadStatusBadge instead — their vocabulary is closed and coloured.
 */
export function StatusBadge({ label }: { label?: string }) {
  if (!label) return <Empty />;
  return <Badge variant="secondary">{label}</Badge>;
}

/*
  ContactLine moved to @command-center/ui-system, beside the record card that is
  its main consumer. Re-exported here so the views that already import it from
  this module keep working, and so there is still exactly one implementation.
*/
export { ContactLine } from '@command-center/ui-system';

export function Field({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || '—'}</dd>
    </div>
  );
}
